import { io } from 'socket.io-client';

// Socket is created lazily by SocketBootstrap via `initSocket(url)`. Before
// that, `socket` is null; emits + listener registrations are queued and
// flushed on init so login-time calls (saveAgentStatus, create-agent-room)
// don't race the bootstrap.

let socket = null;
const pendingEmits = [];
const persistentListeners = new Map();

const drainPendingEmits = () => {
  while (pendingEmits.length > 0) {
    const { event, args } = pendingEmits.shift();
    socket?.emit(event, args);
  }
};

const reattachListeners = () => {
  if (!socket) return;
  for (const [event, handlers] of persistentListeners.entries()) {
    for (const handler of handlers) {
      socket.on(event, handler);
    }
  }
};

export const initSocket = url => {
  if (socket) return socket;
  socket = io(url, { transports: ['websocket'] });
  reattachListeners();
  socket.on('connect', drainPendingEmits);
  return socket;
};

export const getSocket = () => socket;

// `socketEmitter` / `socketListener` / `socketRemover` keep the same call
// signatures so existing call sites work unchanged. If the socket isn't
// initialized yet (early in app boot), emits are queued and listeners are
// registered as "persistent" so they bind once initSocket runs.
export const socketEmitter = (event, args) => {
  if (socket?.connected) {
    socket.emit(event, args);
  } else if (socket) {
    // Created but not connected yet — socket.io-client buffers emits while
    // disconnected, so just emit and let it handle queueing.
    socket.emit(event, args);
  } else {
    pendingEmits.push({ event, args });
  }
};

export const socketListener = (event, handler) => {
  if (!persistentListeners.has(event)) {
    persistentListeners.set(event, new Set());
  }
  const handlersForEvent = persistentListeners.get(event);
  // Dedupe by handler identity. socket.io-client's `on` stacks every
  // registration even if the handler is the same reference, so callers that
  // re-register on every render (or every poll-loop tick) would accumulate
  // listeners. Guarding with this Set check makes socketListener idempotent
  // and bounds the number of handlers per event to the number of distinct
  // handler references.
  if (handlersForEvent.has(handler)) return;
  handlersForEvent.add(handler);
  socket?.on(event, handler);
};

export const socketRemover = (event, handler) => {
  const handlers = persistentListeners.get(event);
  if (handler && handlers) {
    handlers.delete(handler);
    if (handlers.size === 0) persistentListeners.delete(event);
  } else if (!handler) {
    persistentListeners.delete(event);
  }
  if (handler) {
    socket?.off(event, handler);
  } else {
    socket?.off(event);
  }
};

// Exported only for legacy `socket.onAny(...)` usage in InboxV2/index.js.
// Prefer the helpers above for new code.
export { socket };
